<template>
  <form @submit.prevent="handleSubmit">
    <div class="mb-3">
      <label for="title" class="form-label">Title</label>
      <input type="text" class="form-control" id="title" v-model="localSubtask.title" required>
    </div>
    <div class="mb-3">
      <label for="number" class="form-label">Number</label>
      <input type="number" class="form-control" id="number" v-model="localSubtask.number" required>
    </div>
    <div class="mb-3">
      <label for="to_evaluate" class="form-label">Evaluate: </label>
      <input type="checkbox" class="form-check-input form-check-lg" id="to_evaluate" v-model="localSubtask.to_evaluate"><br>
      <small class="form-text text-muted">Uncheck if this task is not to be evaluated.</small>
    </div>
    <div class="mb-3">
      <label for="description" class="form-label">Description</label>
      <textarea type="textarea" class="form-control" id="description" rows="8" v-model="localSubtask.description" required></textarea>
    </div>

    <div v-for="section in fileSections" :key="section.key" class="mb-3">
      <label class="form-label">{{ section.label }}</label>
      <div class="d-flex gap-2 align-items-center">
        <input type="file" class="form-control" :id="section.key" multiple @change="handleFileChange(section.key, $event)">
        <button type="button" class="btn btn-outline-secondary btn-sm text-nowrap" @click="openEditor(section.key, section.defaultName)">
          Create in editor
        </button>
      </div>
      <FileList
        v-if="localSubtask[section.pathsKey] && localSubtask[section.pathsKey].length > 0"
        :title="section.label"
        :files="localSubtask[section.pathsKey]"
        :filesMetadata="localSubtask[section.metaKey]"
        :onViewFile="showFileContent"
        :onDownloadFile="downloadFile"
        :onEditFile="f => editExistingFile(section.key, f)"
        :onDeleteFile="file => deleteFile(file, section.pathsKey)"
      />
      <div v-if="editorLoading && editorType === section.key" class="mt-2 py-3 text-center text-muted">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading file...
      </div>
      <div v-if="editorOpen && editorType === section.key" class="card mt-2 border-primary">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span class="fw-semibold">{{ editorEditingPath ? 'Edit file' : 'Create new file' }} — {{ section.label }}</span>
          <button type="button" class="btn-close" @click="cancelEditor" aria-label="Close"></button>
        </div>
        <div class="card-body">
          <div class="mb-2">
            <label class="form-label small fw-semibold">Filename</label>
            <input type="text" class="form-control form-control-sm font-monospace" v-model="editorFilename" placeholder="script.sh">
          </div>
          <CodeEditor v-model="editorContent" />
          <div class="d-flex gap-2 mt-3">
            <button type="button" class="btn btn-primary btn-sm" :disabled="!editorFilename.trim()" @click="saveFromEditor">
              {{ editorEditingPath ? 'Save' : 'Add file' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelEditor">Cancel</button>
          </div>
        </div>
      </div>
      <div v-if="$data[section.key].length" class="card mt-2">
        <h6 class="card-header">Pending upload</h6>
        <div class="card-body p-0">
          <ul class="list-group list-group-flush">
            <li v-for="(f, i) in $data[section.key]" :key="i" class="list-group-item d-flex justify-content-between align-items-center">
              <span>{{ f.name }}</span>
              <button type="button" class="btn-close" style="font-size:0.65rem" @click="removeFile(section.key, i)" aria-label="Remove"></button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <button type="submit" class="btn btn-primary">{{ buttonText }}</button>
  </form>

  <StemErrorModal ref="stemErrorModal"/>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import SubtaskController from '@/controllers/SubtaskController';
import FileList from '@/components/FilesList.vue';
import StemErrorModal from './StemErrorModal.vue';

export default {
  components: {
    FileList,
    CodeEditor: defineAsyncComponent(() => import('@/components/CodeEditor.vue')),
    StemErrorModal,
  },
  props: {
    subtask: {
      type: Object,
      required: true,
    },
    buttonText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      localSubtask: { ...this.subtask },
      resource_files: [],
      submission_files: [],
      test_files: [],
      precheck_files: [],
      filesToDelete: {
        resource_file_paths: [],
        submission_file_paths: [],
        test_file_paths: [],
        test_pre_check_file_paths: [],
      },
      fileSections: [
        { key: 'resource_files',   pathsKey: 'resource_file_paths',       metaKey: 'resource_files_metadata',       label: 'Resource Files',   defaultName: 'file.sh' },
        { key: 'submission_files', pathsKey: 'submission_file_paths',     metaKey: 'submission_files_metadata',     label: 'Submission Files', defaultName: 'file.sh' },
        { key: 'test_files',       pathsKey: 'test_file_paths',           metaKey: 'test_files_metadata',           label: 'Test Files',       defaultName: 'test_subtask.sh' },
        { key: 'precheck_files',   pathsKey: 'test_pre_check_file_paths', metaKey: 'test_pre_check_files_metadata', label: 'Pre-check Files',  defaultName: 'pre_check_submission.sh' },
      ],
      editorOpen: false,
      editorLoading: false,
      editorType: null,
      editorFilename: '',
      editorContent: '',
      editorOriginalContent: '',
      editorEditingPath: null,
    };
  },
  watch: {
    subtask: {
      handler(newSubtask) {
        this.localSubtask = { ...newSubtask };
      },
      deep: true,
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submitForm', {
        ...this.localSubtask,
        resource_files: this.resource_files,
        submission_files: this.submission_files,
        test_files: this.test_files,
        precheck_files: this.precheck_files,
        filesToDelete: this.filesToDelete,
      });
    },
    confirmDiscard() {
      return this.editorContent === this.editorOriginalContent
        || confirm('Discard unsaved changes?');
    },
    openEditor(type, defaultName) {
      if (!this.confirmDiscard()) return;
      this.editorType = type;
      this.editorEditingPath = null;
      this.editorFilename = defaultName;
      this.editorContent = '#!/bin/bash\n';
      this.editorOriginalContent = '#!/bin/bash\n';
      this.editorOpen = true;
    },
    saveFromEditor() {
      if (!this.editorFilename.trim()) return;
      const name = this.editorFilename.trim();
      const file = new File([this.editorContent], name, { type: 'text/plain' });
      this[this.editorType].push(file);
      if (this.editorEditingPath && name !== this.editorEditingPath.split('/').pop()) {
        // only queue delete when renaming — same-name upload overwrites in place
        const section = this.fileSections.find(s => s.key === this.editorType);
        this.filesToDelete[section.pathsKey].push(this.editorEditingPath);
        this.localSubtask[section.pathsKey] = this.localSubtask[section.pathsKey].filter(p => p !== this.editorEditingPath);
      }
      this.editorEditingPath = null;
      this.editorOpen = false;
      this.editorType = null;
      this.editorFilename = '';
      this.editorContent = '';
      this.editorOriginalContent = '';
    },
    cancelEditor() {
      if (!this.confirmDiscard()) return;
      this.editorOpen = false;
      this.editorType = null;
      this.editorFilename = '';
      this.editorContent = '';
      this.editorOriginalContent = '';
      this.editorEditingPath = null;
    },
    async editExistingFile(type, filePath) {
      if (!this.confirmDiscard()) return;
      this.editorType = type;
      this.editorLoading = true;
      try {
        const response = await SubtaskController.downloadFile(filePath);
        const text = await response.data.text();
        this.editorFilename = filePath.split('/').pop();
        this.editorContent = text;
        this.editorOriginalContent = text;
        this.editorEditingPath = filePath;
        this.editorOpen = true;
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
        this.editorType = null;
      } finally {
        this.editorLoading = false;
      }
    },
    removeFile(type, index) {
      this[type].splice(index, 1);
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
    async showFileContent(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        window.open(URL.createObjectURL(new Blob([response.data])));
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', 'Error downloading file');
      }
    },
    deleteFile(filePath, fileType) {
      this.filesToDelete[fileType].push(filePath);
      this.localSubtask[fileType] = this.localSubtask[fileType].filter(file => file !== filePath);
    },
    handleFileChange(type, event) {
      this[type] = [...this[type], ...event.target.files];
    },
  },
};
</script>
